import PropTypes from "prop-types";
import { useMemo, useState } from "react";

import { t } from "../../services";
import { strCode } from "../../consts";
import { objectUtil } from "../../utils";

import { LoginPageTemplate } from "../../templates";
import {
  Input,
  PasswordValidationInfo,
  SmallButton,
  SVG,
  TextButton,
  WrapperContainer
} from "../../components";

const ResetPassword = ({
  password,
  setPassword,
  newPasswordError,
  isLoading,
  hasError,
  onSubmitResetPassword,
  onResendCode
}) => {
  const [code, setCode] = useState("");
  const [passwordReEnter, setPasswordReEnter] = useState("");

  const disabled = useMemo(() => {
    return (
      isLoading ||
      code.length !== 6 ||
      (newPasswordError && !objectUtil.isEmpty(newPasswordError)) ||
      passwordReEnter.length === 0
    );
  }, [isLoading, code, newPasswordError, passwordReEnter]);

  return (
    <LoginPageTemplate
      icon={<SVG.MailSentIcon />}
      title={t(strCode.RESET_PASSWORD_TITLE)}
      description={t(strCode.RESET_PASSWORD_DESCRIPTION)}>
      <WrapperContainer mb={12}>
        <Input
          id={t(strCode.VERIFICATION_CODE_TITLE)}
          title={t(strCode.VERIFICATION_CODE_TITLE)}
          value={code}
          setValue={setCode}
          placeholder={t(strCode.VERIFICATION_CODE_PLACEHOLDER)}
          maxLength={6}
          type="number"
          hasError={hasError}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !disabled) {
              onSubmitResetPassword(password, passwordReEnter, code);
            }
          }}
        />
      </WrapperContainer>

      <WrapperContainer mb={8}>
        <Input
          id={t(strCode.NEW_PASSWORD_TITLE)}
          title={t(strCode.NEW_PASSWORD_TITLE)}
          value={password}
          setValue={setPassword}
          placeholder={t(strCode.NEW_PASSWORD_PLACEHOLDER)}
          isPassword
          onKeyDown={(event) => {
            if (event.key === "Enter" && !disabled) {
              onSubmitResetPassword(password, passwordReEnter, code);
            }
          }}
        />
      </WrapperContainer>

      <WrapperContainer mb={12}>
        <PasswordValidationInfo passwordValidationErrors={newPasswordError} />
      </WrapperContainer>

      <WrapperContainer mb={32}>
        <Input
          id={t(strCode.RE_ENTER_NEW_PASSWORD_TITLE)}
          title={t(strCode.RE_ENTER_NEW_PASSWORD_TITLE)}
          value={passwordReEnter}
          setValue={setPasswordReEnter}
          placeholder={t(strCode.NEW_PASSWORD_PLACEHOLDER)}
          isPassword
          onKeyDown={(event) => {
            if (event.key === "Enter" && !disabled) {
              onSubmitResetPassword(password, passwordReEnter, code);
            }
          }}
        />
      </WrapperContainer>

      <SmallButton
        onClick={() => onSubmitResetPassword(password, passwordReEnter, code)}
        disabled={disabled}
        loading={isLoading}
        fullWidth>
        {t(strCode.CHANGE_PASSWORD_BTN)}
      </SmallButton>

      <WrapperContainer mt={22}>
        <TextButton
          disabled={hasError}
          onClick={() => {
            setCode("");
            setPasswordReEnter("");
            onResendCode();
          }}>
          {t(strCode.RESEND_VERIFICATION_CODE_BTN)}
        </TextButton>
      </WrapperContainer>
    </LoginPageTemplate>
  );
};

ResetPassword.propTypes = {
  password: PropTypes.string.isRequired,
  setPassword: PropTypes.func.isRequired,
  newPasswordError: PropTypes.shape({
    isValidPasswordLength: PropTypes.bool,
    isValidPasswordSymbol: PropTypes.bool,
    isValidPasswordBigSymbol: PropTypes.bool,
    isValidPasswordNumber: PropTypes.bool
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  hasError: PropTypes.bool.isRequired,
  onSubmitResetPassword: PropTypes.func.isRequired,
  onResendCode: PropTypes.func.isRequired
};

export default ResetPassword;
